'use client'

/**
 * Purpose: ZXing continuous barcode decode on a video element.
 * Inputs: videoRef, onDecode, optional onDetect (fires first, while the frame is still live),
 * decodePaused guard.
 * Outputs: start/stop, hasTorch, toggleTorch, controlsRef. Requests continuous autofocus on the
 * rear camera track where the browser exposes `focusMode` (Chrome/Android); iOS focuses natively.
 */

import { useCallback, useRef, useState } from 'react'
import { BarcodeFormat, BrowserMultiFormatReader } from '@zxing/browser'
import type { BarcodeSymbology } from '@lib/models/types'
import { boxFromPoints, type NormalizedBox } from '@lib/barcode-frame'
import { errorMessage, logger } from '@lib/logger'

// The six retail symbologies this app acts on; everything else (shipping codes, etc.) is
// `unsupported` — see lib/gtin.ts, design.md §3. No `POSSIBLE_FORMATS` hint is passed to ZXing
// itself: a restricted reader would silently ignore a shipping code, with no way to tell the
// user why nothing happened.
const FORMAT_TO_SYMBOLOGY: Partial<Record<BarcodeFormat, BarcodeSymbology>> = {
  [BarcodeFormat.EAN_13]: 'ean_13',
  [BarcodeFormat.EAN_8]: 'ean_8',
  [BarcodeFormat.UPC_A]: 'upc_a',
  [BarcodeFormat.UPC_E]: 'upc_e',
  [BarcodeFormat.QR_CODE]: 'qr_code',
  [BarcodeFormat.DATA_MATRIX]: 'data_matrix',
}

function toSymbology(format: BarcodeFormat): BarcodeSymbology {
  return FORMAT_TO_SYMBOLOGY[format] ?? 'unsupported'
}

export type UseBarcodeScannerOptions = {
  videoRef: React.RefObject<HTMLVideoElement | null>
  onDecode: (barcode: string, symbology: BarcodeSymbology) => void | Promise<void>
  /**
   * Called synchronously on a successful read, before the stream is stopped, with where the code
   * sits in the video frame (null when ZXing gave no usable points). The caller can still draw
   * the live frame here — once the stream stops some browsers blank the video.
   */
  onDetect?: (box: NormalizedBox | null) => void
  decodePaused?: boolean
  facingMode?: 'user' | 'environment'
  idealWidth?: number
  idealHeight?: number
}

export function useBarcodeScanner({
  videoRef,
  onDecode,
  onDetect,
  decodePaused = false,
  facingMode = 'environment',
  idealWidth = 1920,
  idealHeight = 1080,
}: UseBarcodeScannerOptions) {
  // Refs guard against double-decode and hold ZXing stop controls.
  const readerRef = useRef<BrowserMultiFormatReader | null>(null)
  const controlsRef = useRef<{ stop: () => void } | null>(null)
  const decodedRef = useRef(false)
  // Torch support is probed after the stream attaches — not all devices expose it.
  const [hasTorch, setHasTorch] = useState(false)
  const [torchOn, setTorchOn] = useState(false)

  // Tear down ZXing controls and reset first-decode guard.
  const stop = useCallback(() => {
    controlsRef.current?.stop()
    controlsRef.current = null
    decodedRef.current = false
    setTorchOn(false)
  }, [])

  const start = useCallback(async () => {
    if (decodePaused) return
    stop()
    const reader = new BrowserMultiFormatReader()
    readerRef.current = reader
    const video = videoRef.current
    if (!video) return

    // Continuous decode — stops after first successful read or when stop() is called.
    const controls = await reader.decodeFromConstraints(
      {
        video: {
          facingMode: { ideal: facingMode },
          width: { ideal: idealWidth },
          height: { ideal: idealHeight },
        },
      },
      video,
      async (result) => {
        if (decodedRef.current || decodePaused) return
        if (result) {
          decodedRef.current = true
          // Report the code's position while the video still holds the frame it was read from.
          onDetect?.(
            boxFromPoints(result.getResultPoints?.(), video.videoWidth, video.videoHeight),
          )
          controls.stop()
          controlsRef.current = null
          await onDecode(result.getText(), toSymbology(result.getBarcodeFormat()))
        }
      },
    )
    controlsRef.current = controls
    // Torch is optional — probe track capabilities after stream attaches.
    const track = (video.srcObject as MediaStream | null)?.getVideoTracks()[0]
    if (track) {
      const caps = track.getCapabilities() as MediaTrackCapabilities & {
        torch?: boolean
        focusMode?: string[]
      }
      setHasTorch(!!caps.torch)
      // getUserMedia often leaves Android cameras on a fixed/single-shot focus, so close-up
      // barcodes stay blurry — ask for continuous autofocus when the device supports it.
      if (caps.focusMode?.includes('continuous')) {
        try {
          await track.applyConstraints({
            advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet],
          })
        } catch (err) {
          logger.warn('use-barcode-scanner', 'continuous focus unsupported', { error: errorMessage(err) })
        }
      }
    }
  }, [decodePaused, facingMode, idealHeight, idealWidth, onDecode, onDetect, stop, videoRef])

  // Allow a new decode after label capture blocked the reader.
  const resetDecodeGuard = useCallback(() => {
    decodedRef.current = false
  }, [])

  const blockDecode = useCallback(() => {
    decodedRef.current = true
  }, [])

  const toggleTorch = useCallback(async () => {
    const video = videoRef.current
    if (!video?.srcObject) return
    // Torch uses MediaTrackConstraints.advanced — unsupported on many front cameras.
    const stream = video.srcObject as MediaStream
    const track = stream.getVideoTracks()[0]
    if (!track) return
    try {
      // advanced constraints — not all rear cameras expose torch.
      await track.applyConstraints({ advanced: [{ torch: !torchOn } as MediaTrackConstraintSet] })
      setTorchOn((prev) => !prev)
    } catch {
      // torch not supported
    }
  }, [torchOn, videoRef])

  return {
    start,
    stop,
    hasTorch,
    torchOn,
    toggleTorch,
    resetDecodeGuard,
    blockDecode,
    controlsRef,
  }
}
